/**
 * Purchase Order Model - Processes API responses for purchase order data
 */

/**
 * Process purchase order list response data
 * @param {Object} response - API response from purchase order list endpoint
 * @returns {Object} Formatted purchase order list data
 */
export const getPurchaseOrders = (response) => {
  if (response && response.data && response.data.success === true) {
    const purchaseOrders = Array.isArray(response.data.data)
      ? response.data.data.map((po) => ({
          id: po.id,
          slack: po.slack,
          po_number: po.po_number,
          po_reference: po.po_reference,
          supplier_id: po.supplier_id,
          supplier_name: po.supplier_name,
          order_date: po.order_date,
          order_due_date: po.order_due_date,
          currency_code: po.currency_code,
          currency_name: po.currency_name,
          shipping_charge: po.shipping_charge,
          packing_charge: po.packing_charge,
          total_order_amount: po.total_order_amount,
          terms: po.terms,
          tax_option: po.tax_option,
          update_stock: po.update_stock,
          status: po.status,
          status_data: po.status_data
            ? {
                id: po.status_data.id,
                value: po.status_data.value,
                label: po.status_data.label
              }
            : null,
          products: po.products || [],
          total_order_amount_actual: po.total_order_amount_actual || null,
          // Preserve both snake_case (API) and camelCase for compatibility
          created_at: po.created_at,
          updated_at: po.updated_at,
          createdAt: po.createdAt || po.created_at,
          updatedAt: po.updatedAt || po.updated_at,
          created_by: po.created_by,
          updated_by: po.updated_by,
          created_user: po.created_user
            ? {
                id: po.created_user.id,
                fullname: po.created_user.fullname
              }
            : null,
          updated_user: po.updated_user
            ? {
                id: po.updated_user.id,
                fullname: po.updated_user.fullname
              }
            : null
        }))
      : []

    return {
      success: true,
      total: response.data.total || purchaseOrders.length,
      data: purchaseOrders
    }
  }
  return {
    success: false,
    message: response?.data?.message || 'Failed to fetch purchase order list',
    data: []
  }
}

/**
 * Process single purchase order details response
 * @param {Object} response - API response from purchase order details endpoint
 * @returns {Object} Formatted purchase order details
 */
export const getPurchaseOrder = (response) => {
  if (response && response.data && response.data.success === true) {
    const item = response.data.data || {}
    return {
      success: true,
      data: item
    }
  }
  return {
    success: false,
    message:
      response?.data?.message || 'Failed to fetch purchase order details',
    data: {}
  }
}

/**
 * Process purchase order creation response
 * @param {Object} response - API response from purchase order create endpoint
 * @returns {Object} Formatted creation response
 */
export const createPurchaseOrder = (response) => {
  if (response && response.data) {
    return {
      success: response.data.success === true,
      message: response.data.message || 'Purchase order created successfully',
      data: response.data.data || null
    }
  }
  return {
    success: false,
    message: 'Failed to create purchase order',
    data: null
  }
}

/**
 * Process purchase order update response
 * @param {Object} response - API response from purchase order update endpoint
 * @returns {Object} Formatted update response
 */
export const updatePurchaseOrder = (response) => {
  if (response && response.data) {
    return {
      success: response.data.success === true,
      message: response.data.message || 'Purchase order updated successfully',
      data: response.data.data || null
    }
  }
  return {
    success: false,
    message: 'Failed to update purchase order',
    data: null
  }
}

/**
 * Process purchase order status update response
 * @param {Object} response - API response from purchase order status update endpoint
 * @returns {Object} Formatted status update response
 */
export const updatePurchaseOrderStatus = (response) => {
  if (response && response.data) {
    return {
      success: response.data.success === true,
      message:
        response.data.message || 'Purchase order status updated successfully',
      data: response.data.data || null
    }
  }
  return {
    success: false,
    message: 'Failed to update status',
    data: null
  }
}

/**
 * Process purchase order delete response
 * @param {Object} response - API response from purchase order delete endpoint
 * @returns {Object} Formatted delete response
 */
export const deletePurchaseOrder = (response) => {
  if (response && response.data) {
    return {
      success: response.data.success === true,
      message: response.data.message || 'Purchase order deleted successfully'
    }
  }
  return {
    success: false,
    message: 'Failed to delete purchase order'
  }
}

/**
 * Process invoice generation from purchase order response
 * @param {Object} response - API response from invoice generation endpoint
 * @returns {Object} Formatted invoice generation response
 */
export const generateInvoiceFromPO = (response) => {
  if (response && response.data) {
    return {
      success: response.data.success === true,
      message: response.data.message || 'Invoice generated successfully',
      data: response.data.data || null
    }
  }
  return {
    success: false,
    message: response?.data?.message || 'Failed to generate invoice'
  }
}

/**
 * Process products loading for purchase order response
 * @param {Object} response - API response from products loading endpoint
 * @returns {Object} Formatted products loading response
 */
export const loadProductsForPO = (response) => {
  if (response && response.success === true && response.data) {
    return {
      success: true,
      message: response.message || 'Products loaded successfully',
      data: response.data || []
    }
  }
  return {
    success: false,
    message: response?.message || 'Failed to load products',
    data: []
  }
}

export const supplierListGet = (response) => {
  // Based on Postman collection response format
  if (response && response.data && response.data.success === true) {
    // Map the API response to our frontend model
    const suppliers = Array.isArray(response.data.data) ? response.data.data.map(supplier => ({
      id: supplier.id,
      slack: supplier.slack,
      supplier_code: supplier.supplier_code,
      name: supplier.name,
      email: supplier.email,
      phone: supplier.phone,
      address: supplier.address,
      pincode: supplier.pincode,
      status: supplier.status,
      status_data: supplier.status_data ? {
        id: supplier.status_data.id,
        value: supplier.status_data.value,
        label: supplier.status_data.label
      } : null,
      
    })) : [];
    
    return {
      success: true,
      total: response.data.total || suppliers.length,
      data: suppliers
    };
  }
  return {
    success: false,
    message: response?.data?.message || 'Failed to fetch supplier list',
    data: []
  };
};
